// React and Hooks
import React from 'react';
// Actions
// Selectors
// Utils
import PropTypes from 'prop-types';
// Components

import { Modal, Icon, palette } from '@m12s/component-library';
import { falTimes } from 'lib/icons';
import {
  IconWrapper,
  modalStyles,
  headerStyles,
  detailsStyles,
  buttonStyles,
  ModalButtons,
} from './styled';

/**
 * BaseModal component that provides a consistent modal layout with header, details, and buttons
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls the visibility of the modal
 * @param {Function} props.onClose - Callback function to handle modal close action
 * @param {React.ReactNode} props.header - Content to be displayed in the modal header
 * @param {React.ReactNode} [props.details] - Optional content to be displayed in the modal body
 * @param {React.ReactNode|React.ReactNode[]} props.buttons - Buttons to be displayed in the modal footer (wrapped in ModalButtons)
 * @param {React.ReactNode} [props.footer] - Custom footer content passed directly without wrapping (takes precedence over buttons)
 * @returns {React.ReactElement} BaseModal component
 */
const BaseModal = ({ isOpen, onClose, header, details, buttons, footer }) => {
  const buttonsContent =
    footer || (buttons && <ModalButtons>{buttons}</ModalButtons>);
  return (
    <Modal
      isOpen={isOpen}
      modalStyles={modalStyles}
      headerStyles={headerStyles}
      detailsStyles={detailsStyles}
      buttonsStyles={buttonStyles}
      headerIcon={
        <IconWrapper onClick={onClose}>
          <Icon
            size="2rem"
            color={palette.Grey700}
            icon={falTimes}
            cursor="pointer"
          />
        </IconWrapper>
      }
      header={header}
      details={details}
      {...(buttonsContent && { buttons: buttonsContent })}
    />
  );
};

BaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  header: PropTypes.node.isRequired,
  buttons: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  footer: PropTypes.node,
  details: PropTypes.node,
};

BaseModal.defaultProps = {
  details: null,
};

export default BaseModal;
